// Visible Google reCAPTCHA v2 ("I'm not a robot") helper.
//
// The checkbox is only rendered after repeated failed logins (the backend
// flags `captchaRequired`), so a normal sign-in is never interrupted. Once
// shown, the user must tick the box; the resulting token is sent with the
// next login attempt and verified server-side.
//
// If REACT_APP_RECAPTCHA_SITE_KEY is unset (e.g. local dev), every export is a
// no-op — no widget renders and getCheckboxResponse() returns null. The
// backend treats a missing token / unconfigured captcha as "skip".

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

let loadPromise = null;
let widgetId = null;

export function isCaptchaConfigured() {
  return !!SITE_KEY;
}

// Lazily inject the reCAPTCHA v2 API (explicit-render mode) exactly once.
// Resolves with the grecaptcha global, or null if unconfigured / load failed.
export function loadRecaptcha() {
  if (!SITE_KEY) return Promise.resolve(null);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve(window.grecaptcha);
      return;
    }
    // Global callback the reCAPTCHA script invokes once it's ready.
    window.__nuveOnRecaptchaLoad = () => resolve(window.grecaptcha || null);
    const script = document.createElement('script');
    script.src =
      'https://www.google.com/recaptcha/api.js?onload=__nuveOnRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loadPromise = null; // allow a later retry
      resolve(null);
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

// Render the checkbox into `container` (a DOM node). Safe to call repeatedly —
// if a widget already exists it's just reset. Returns the widget id or null.
export async function renderCheckbox(container) {
  if (!SITE_KEY || !container) return null;
  const grecaptcha = await loadRecaptcha();
  if (!grecaptcha) return null;

  if (widgetId !== null) {
    try {
      grecaptcha.reset(widgetId);
    } catch (e) {
      /* widget went away (e.g. container remounted) — re-render below */
      widgetId = null;
    }
  }
  if (widgetId === null) {
    try {
      widgetId = grecaptcha.render(container, { sitekey: SITE_KEY });
    } catch (e) {
      return null;
    }
  }
  return widgetId;
}

// The token from a solved checkbox, or null if not configured / not solved.
export function getCheckboxResponse() {
  if (!SITE_KEY || widgetId === null || !window.grecaptcha) return null;
  try {
    return window.grecaptcha.getResponse(widgetId) || null;
  } catch (e) {
    return null;
  }
}

// Clear the checkbox so the user must solve it again (v2 tokens are single-use).
export function resetCheckbox() {
  if (widgetId !== null && window.grecaptcha) {
    try {
      window.grecaptcha.reset(widgetId);
    } catch (e) {
      /* no-op */
    }
  }
}
